import "./Forms.scss";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import PageTitle from "../../../components/PageTitle/PageTitle";
import AlertModal from "../../../components/AlertModal/AlertModal";
import {
  DynamicInput,
  DynamicSelect,
  DynamicTextarea,
  DynamicCheckbox,
} from "../../../components/Inputs/Inputs";
import { NormalizeApiError } from "../../../utils/normalizeApiError";
import Button from "../../../components/Button/Button";
import { AuthContext } from "../../../contexts/AuthContext";

function formatDate(dateObj, format) {
  const yyyy = dateObj.getFullYear().toString();
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");

  switch (format) {
    case "YYYY-MM-DD":
      return `${yyyy}-${mm}-${dd}`;
    case "DD-MM-YYYY":
      return `${dd}-${mm}-${yyyy}`;
    case "YYYYMMDD":
      return `${yyyy}${mm}${dd}`;
    case "DDMMYYYY":
      return `${dd}${mm}${yyyy}`;
  }
}

const EMPTY_FORM = {
  id: null,
  group_id: null,
  hotel_id: null,
  provider_id: null,
  provider_key: "",
  provider_hotel_id: "",
  hotel_name: "",
  url: "",
  date_format: "YYYY-MM-DD",
  allow_infants: false,
  allow_promocode: false,
};

export default function Forms() {
  const {
    axiosAuth,
    accessibleGroups = [],
    currentUser,
    accessibleHotels = [],
  } = useContext(AuthContext);
  const isHotelAdmin = currentUser?.role === "hotel_admin";

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [hotels, setHotels] = useState([]);
  const [selectedHotelId, setSelectedHotelId] = useState("");

  // providers + form
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formReady, setFormReady] = useState(false);

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  // opções dos selects
  const groupOptions = useMemo(
    () => accessibleGroups.map((g) => ({ value: String(g.id), label: g.name })),
    [accessibleGroups]
  );

  const hotelOptions = useMemo(() => {
    let list = hotels;
    if (isHotelAdmin && accessibleHotels?.length) {
      const allowed = new Set(accessibleHotels.map((h) => h.id));
      list = list.filter((h) => allowed.has(h.id));
    }
    return list.map((h) => ({ value: String(h.id), label: h.name }));
  }, [hotels, isHotelAdmin, accessibleHotels]);

  // carregar providers
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosAuth.get("/booking-providers/");
        setProviders(Array.isArray(data) ? data : []);
      } catch (err) {
        openError(
          "Erro ao carregar providers",
          err?.response?.data?.detail || err.message
        );
      }
    })();
  }, [axiosAuth, openError]);

  // quando escolhe grupo, carrega hotéis de grupo
  useEffect(() => {
    setSelectedHotelId("");
    setForm({
      ...EMPTY_FORM,
      group_id: selectedGroupId ? Number(selectedGroupId) : null,
    });
    setFormReady(false);
    setHotels([]);

    if (!selectedGroupId) return;

    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/hotels/group/${selectedGroupId}`
        );
        const list = Array.isArray(data) ? data : [];
        setHotels(list);

        // auto-seleciona o 1º hotel do grupo
        if (list.length > 0) {
          setSelectedHotelId(String(list[0].id));
        }
      } catch (err) {
        openError(
          "Erro ao carregar hotéis",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedGroupId, axiosAuth, openError]);

  useEffect(() => {
    if (accessibleGroups.length > 1 && !selectedGroupId) {
      setSelectedGroupId(String(accessibleGroups[0]?.id));
    }
  }, [accessibleGroups, selectedGroupId]);

  // carregar (ou iniciar) o formulário do par group_id + hotel_id
  useEffect(() => {
    // reset imediato
    setForm({
      ...EMPTY_FORM,
      group_id: selectedGroupId ? Number(selectedGroupId) : null,
      hotel_id: selectedHotelId ? Number(selectedHotelId) : null,
    });
    setFormReady(false);

    if (!selectedGroupId || !selectedHotelId) return;

    (async () => {
      setLoading(true);
      try {
        // ✅ usa endpoint por hotel
        const { data } = await axiosAuth.get(
          `/booking-forms/hotel/${selectedHotelId}`
        );
        const list = Array.isArray(data) ? data : [];

        // segurança no cliente: tenta match exato por group_id também
        const match =
          list.find(
            (f) =>
              String(f.group_id) === String(selectedGroupId) &&
              String(f.hotel_id) === String(selectedHotelId)
          ) || list[0];

        if (match) {
          const prov = providers.find((p) => p.id === match.provider_id);
          setForm({
            id: match.id,
            group_id: match.group_id,
            hotel_id: match.hotel_id,
            provider_id: match.provider_id,
            provider_key: prov?.key || "", // para preencher o <select>
            provider_hotel_id: match.provider_hotel_id || "",
            hotel_name: match.hotel_name || "",
            url: match.url || "",
            date_format: match.date_format || prov?.date_format || "YYYY-MM-DD",
            allow_infants: !!match.allow_infants,
            allow_promocode: !!match.allow_promocode,
          });
        } else {
          // não existe form para este hotel → defaults com 1º provider
          const first = providers[0] || {};
          setForm({
            ...EMPTY_FORM,
            group_id: Number(selectedGroupId),
            hotel_id: Number(selectedHotelId),
            provider_id: first?.id ?? null,
            provider_key: first?.key || "",
            date_format: first?.date_format || "YYYY-MM-DD",
          });
        }
        setFormReady(true);
      } catch (err) {
        if (err?.response?.status === 404) {
          // hotel sem form → defaults
          const first = providers[0] || {};
          setForm({
            ...EMPTY_FORM,
            group_id: Number(selectedGroupId),
            hotel_id: Number(selectedHotelId),
            provider_id: first?.id ?? null,
            provider_key: first?.key || "",
            date_format: first?.date_format || "YYYY-MM-DD",
          });
          setFormReady(true);
        } else {
          openError(
            "Erro ao carregar configuração do formulário",
            err?.response?.data?.detail || err.message
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedGroupId, selectedHotelId, providers, axiosAuth, openError]);

  // handlers
  const handleChange = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const handleProviderChange = useCallback(
    (e) => {
      const nextKey = e.target?.value ?? e; // compat com DynamicSelect
      const p = providers.find((x) => x.key === nextKey);
      setForm((f) => ({
        ...f,
        provider_key: nextKey,
        provider_id: p?.id ?? null,
        date_format: p?.date_format || f.date_format || "YYYY-MM-DD",
      }));
    },
    [providers]
  );

  const saveForm = useCallback(async () => {
    // validações básicas
    if (!form.group_id || !form.hotel_id) {
      openError("Validação", "Selecione um grupo e um hotel.");
      return;
    }
    const prov =
      providers.find((p) => p.key === form.provider_key) ||
      providers.find((p) => p.id === form.provider_id);
    if (!prov?.id) {
      openError("Validação", "Escolha um provider válido.");
      return;
    }
    if (!form.url?.trim()) {
      openError("Validação", "Preencha o URL template.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        group_id: Number(form.group_id),
        hotel_id: Number(form.hotel_id),
        provider_id: Number(prov.id),
        provider_key: prov.key,
        provider_hotel_id: String(form.provider_hotel_id || ""),
        hotel_name: String(form.hotel_name || ""),
        url: String(form.url || ""),
        date_format: form.date_format || "YYYY-MM-DD",
        allow_infants: Boolean(form.allow_infants),
        allow_promocode: Boolean(form.allow_promocode),
      };

      if (form.id) {
        const { data } = await axiosAuth.put(
          `/booking-forms/${form.id}`,
          payload
        );
        setForm({
          ...data,
          provider_key: prov.key, // mantém o select coerente
        });
      } else {
        const { data } = await axiosAuth.post("/booking-forms/", payload);
        setForm({
          ...data,
          provider_key: prov.key,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const friendly = NormalizeApiError(detail) || err.message;
      openError("Erro ao guardar o formulário", friendly);
    } finally {
      setSaving(false);
    }
  }, [form, providers, axiosAuth, openError]);

  // preview do URL
  const exampleUrl = useMemo(() => {
    if (!form?.url) return "";
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);

    const map = {
      hotel_name: form.hotel_name || "",
      hotel_id: form.provider_hotel_id || "",
      checkin_date: formatDate(now, form.date_format),
      checkout_date: formatDate(tomorrow, form.date_format),
      adults: 2,
      children: 1,
      infants: form.allow_infants ? 0 : "",
      promocode: form.allow_promocode ? "PROMO123" : "",
    };

    return form.url.replace(/\{(\w+)\}/g, (_, key) => map[key] ?? "");
  }, [form]);

  return (
    <div className="forms">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle title="Definições de Formulários" />

      <div className="filters">
        <DynamicSelect
          label="Grupo"
          placeholder="Escolher um grupo"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          options={groupOptions}
          disabled={loading}
        />
        <DynamicSelect
          label="Hotel"
          placeholder="Escolher um hotel"
          value={selectedHotelId}
          onChange={(e) => setSelectedHotelId(e.target.value)}
          options={hotelOptions}
          disabled={loading || !selectedGroupId}
        />
      </div>

      <div className="container">
        {selectedGroupId && selectedHotelId && formReady && (
          <div className="form-editor">
            <div className="row two-col">
              <DynamicInput
                type="text"
                label="Nome do booking engine"
                name="hotel_name"
                value={form.hotel_name ?? ""}
                onChange={handleChange}
              />
              <DynamicInput
                type="text"
                label="ID do hotel no provider"
                name="provider_hotel_id"
                value={form.provider_hotel_id ?? ""}
                onChange={handleChange}
              />
            </div>

            <div className="row">
              <DynamicSelect
                label="Provedor"
                name="provider_key"
                value={form.provider_key ?? ""}
                onChange={handleProviderChange}
                options={providers.map((p) => ({
                  value: p.key,
                  label: p.name,
                }))}
                placeholder="Escolher um provedor"
              />
            </div>

            <div className="row">
              <div className="panel-tag">
                <p>
                  Nome do hotel: <code>{`{hotel_name}`}</code>
                </p>
                <p>
                  ID do hotel: <code>{`{hotel_id}`}</code>
                </p>
                <p>
                  Adultos: <code>{`{adults}`}</code>
                </p>
                <p>
                  Crianças: <code>{`{children}`}</code>
                </p>
                <p>
                  Bebés: <code>{`{infants}`}</code> (deixar vazio se não
                  permitir)
                </p>
                <p>
                  Promocode: <code>{`{promocode}`}</code> (deixar vazio se não
                  permitir)
                </p>
                <p>
                  Check-in: <code>{`{checkin_date}`}</code>
                </p>
                <p>
                  Check-out: <code>{`{checkout_date}`}</code>
                </p>
              </div>
              <DynamicTextarea
                name="url"
                rows={3}
                value={form.url ?? ""}
                onChange={handleChange}
                label="URL template"
              />
            </div>

            <div className="row">
              <DynamicSelect
                label="Formato da data"
                name="date_format"
                value={form.date_format}
                onChange={handleChange}
                options={[
                  { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
                  { value: "DD-MM-YYYY", label: "DD-MM-YYYY" },
                  { value: "YYYYMMDD", label: "YYYYMMDD" },
                  { value: "DDMMYYYY", label: "DDMMYYYY" },
                ]}
              />
            </div>

            <div className="row">
              <div className="checkboxes">
                <DynamicCheckbox
                  label="Permitir bebés"
                  name="allow_infants"
                  checked={!!form.allow_infants}
                  onChange={(n, v) => handleChange(n, Boolean(v))}
                />
                <DynamicCheckbox
                  label="Permitir promocode"
                  name="allow_promocode"
                  checked={!!form.allow_promocode}
                  onChange={(n, v) => handleChange(n, Boolean(v))}
                />
              </div>
            </div>

            <div className="save-all">
              <Button
                onClick={saveForm}
                type="button"
                disabled={saving || loading}
              >
                Guardar
              </Button>
            </div>

            <div className="row">
              <p>Exemplo de Url preenchido:</p>
              <div className="url-preview">
                <code className="example-url">{exampleUrl || "-"}</code>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
